import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface CategoryFilterProps {
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
}

export const CategoryFilter = ({ selectedCategory, onCategoryChange }: CategoryFilterProps) => {
  const { data: dbCategories = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('name')
        .order('name');
      
      if (error) {
        console.error('Error fetching categories:', error);
        return [];
      }
      
      return data.map(category => category.name);
    },
  });

  // Combine "الكل" with database categories
  const categories = ["الكل", ...dbCategories];

  const handleCategoryClick = (category: string) => {
    if (category === selectedCategory) {
      onCategoryChange("الكل");
    } else {
      onCategoryChange(category);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-4xl mx-auto px-2 sm:px-4">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white/80 dark:bg-gray-800/90 rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-lg backdrop-blur-sm border border-gray-100 dark:border-gray-700/50"
        >
          <div className="flex justify-center items-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto px-2 sm:px-4">
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white/80 dark:bg-gray-800/90 rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-lg backdrop-blur-sm border border-gray-100 dark:border-gray-700/50"
      >
        <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-primary dark:text-white font-arabic">التصنيفات</h3>
        <ScrollArea className="w-full" dir="rtl">
          <div className="flex gap-2 sm:gap-3 px-1 pb-4">
            {categories.map((category, index) => (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  type="button"
                  variant={selectedCategory === category ? "default" : "outline"}
                  className={`font-arabic transition-all duration-300 text-sm sm:text-base px-3 py-1.5 sm:px-4 sm:py-2 whitespace-nowrap ${
                    selectedCategory === category 
                      ? "bg-primary hover:bg-primary/90 text-white shadow-md"
                      : "bg-white dark:bg-gray-700 hover:bg-primary/10 dark:hover:bg-primary/20 border-gray-200 dark:border-gray-600"
                  }`}
                  onClick={() => handleCategoryClick(category)}
                >
                  {category}
                </Button>
              </motion.div>
            ))}
          </div>
          <ScrollBar orientation="horizontal" className="h-2" />
        </ScrollArea>
      </motion.div>
    </div>
  );
};